<script setup lang="ts">
import { toTypedSchema } from '@vee-validate/valibot';
import { CONTACT_PROJECT_TYPE_VALUES, ContactFormSchema, type ContactForm } from '@@/shared/contact/types';
import type { PropertyOnPage } from '@@/shared/property/api-types';

const props = withDefaults(
  defineProps<{
    id: string
    property?: PropertyOnPage
    variant?: `default` | `property`
  }>(),
  {
    variant: `default`,
  },
);

const emit = defineEmits<{
  'update:form': [form: ContactForm]
}>();
const { t, locale } = useI18n();
const { m } = useIadFormat();

const initialValues: ContactForm = {
  project: props.property?.transactionType === 'rental' ? 'rent' : 'buy',
  locale: locale.value,
  firstName: '',
  lastName: '',
  email: '',
  message: '',
  phone: '',
  adSubscription: false,
  adPartnerSubscription: false,
};

const { handleSubmit, resetForm } = useForm({
  initialValues,
  validationSchema: toTypedSchema(ContactFormSchema),
});

// We need to maintain these composables to prevent direct computation and display of error ref
const { value: project, errorMessage: errorMessageProject } = useField<string>(`project`);
const errorProject = computed(() => errorMessageProject.value ? t(errorMessageProject.value) : undefined);
const { notifyError } = useNotifications();

const displayMessageArea = ref(false);
const onSubmit = handleSubmit((values: ContactForm) => {
  emit(`update:form`, values);
  displayMessageArea.value = false;
  resetForm();
}, () => {
  notifyError(t('core.form.error.enter_mandatory_fields'));
});
</script>

<template>
  <form
    :id
    novalidate
    autocomplete="off"
    @submit="onSubmit"
  >
    <div>
      <div v-if="variant === 'default'">
        <IadLabel
          html-for="project"
          :text="m('agent.form.label_for_project')"
        />
        <IadRadioGroup
          v-model="project"
          :error="errorProject"
        >
          <IadRadio
            v-for="projectTypeValue in CONTACT_PROJECT_TYPE_VALUES"
            :key="projectTypeValue"
            :label="$t(`core.navigation.${projectTypeValue}`)"
            :value="projectTypeValue"
          />
        </IadRadioGroup>
      </div>
      <AgentContactInfoFields :id />
    </div>
  </form>
</template>
